import React, { useState } from 'react';
import {
  Modal,
  Button,
  RadioGroup,
  Radio,
  Message,
} from 'rsuite';

const FORMAT_ARENA = 'arena';
const FORMAT_PLAIN = 'plain';
const FORMAT_CSV_MOXFIELD = 'csv_moxfield';
const FORMAT_CSV_DECKBOX = 'csv_deckbox';

const COPY_RESET_DELAY_MS = 2000;

const COLORS = {
  muted: '#aaa',
  white: '#fff',
};

const SPACING = {
  marginBottom: 16,
  labelMarginBottom: 8,
};

const FONT = {
  labelSize: 13,
  warningSize: 13,
};

// --- CSV helpers ---

const FORMAT_DECKBOX = 'deckbox';
const FORMAT_MOXFIELD = 'moxfield';

const DECKBOX_HEADERS = ['Count', 'Name', 'Edition', 'Condition', 'Language', 'Foil', 'Tags', 'My Price'];
const MOXFIELD_HEADERS = ['Count', 'Name', 'Edition', 'Condition', 'Purchase Price', 'Section', 'Foil'];

function escapeCSV(value) {
  const str = value == null ? '' : String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return '"' + str.replace(/"/g, '""') + '"';
  }
  return str;
}

function finishToDeckboxFoil(finish) {
  if (finish === 'foil') return 'Foil';
  if (finish === 'etched') return 'Etched';
  return '';
}

function finishToMoxfieldFoil(finish) {
  if (finish === 'foil') return 'Foil';
  if (finish === 'etched') return 'Etched';
  return '';
}

function mapConditionDeckbox(condition) {
  const map = {
    MINT: 'Mint',
    NM: 'Near Mint',
    LP: 'Lightly Played',
    MP: 'Moderately Played',
    HP: 'Heavily Played',
    DAMAGED: 'Damaged',
  };
  return map[condition] || 'Near Mint';
}

function mapConditionMoxfield(condition) {
  const map = {
    MINT: 'Mint',
    NM: 'Near Mint',
    LP: 'Lightly Played',
    MP: 'Moderately Played',
    HP: 'Heavily Played',
    DAMAGED: 'Damaged',
  };
  return map[condition] || 'Near Mint';
}

function buildCSV(entries, csvFormat) {
  const rows = [];
  if (csvFormat === FORMAT_DECKBOX) {
    rows.push(DECKBOX_HEADERS.map(escapeCSV).join(','));
    entries.forEach((entry) => {
      rows.push([
        entry.quantity || 1,
        escapeCSV(entry.name || ''),
        escapeCSV(entry.set_name || ''),
        escapeCSV(mapConditionDeckbox(entry.condition)),
        'English',
        finishToDeckboxFoil(entry.finish || 'nonfoil'),
        entry.is_sideboard ? 'Sideboard' : '',
        entry.purchase_price_raw != null ? entry.purchase_price_raw.toFixed(2) : '',
      ].join(','));
    });
  } else {
    rows.push(MOXFIELD_HEADERS.map(escapeCSV).join(','));
    entries.forEach((entry) => {
      rows.push([
        entry.quantity || 1,
        escapeCSV(entry.name || ''),
        escapeCSV(entry.set_name || ''),
        escapeCSV(mapConditionMoxfield(entry.condition)),
        entry.purchase_price_raw != null ? entry.purchase_price_raw.toFixed(2) : '',
        entry.is_sideboard ? 'sideboard' : 'mainboard',
        finishToMoxfieldFoil(entry.finish || 'nonfoil'),
      ].join(','));
    });
  }
  return rows.join('\n');
}

function downloadCSV(csvContent, filename) {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// --- Deck list helpers ---

function buildArenaLine(entry) {
  const qty = entry.quantity || 1;
  const name = entry.name || '';
  const setCode = (entry.set_code || '').toUpperCase();
  const collectorNumber = entry.collector_number || '';
  return `${qty} ${name} (${setCode}) ${collectorNumber}`;
}

function buildPlainLine(entry) {
  const qty = entry.quantity || 1;
  const name = entry.name || '';
  return `${qty} ${name}`;
}

function buildArenaOutput(entries) {
  const commanders = entries.filter((e) => e.is_commander === true);
  const sideboard = entries.filter((e) => e.is_sideboard === true && e.is_commander !== true);
  const deck = entries.filter((e) => e.is_commander !== true && e.is_sideboard !== true);

  const sections = [];
  if (commanders.length > 0) {
    sections.push('Commander');
    commanders.forEach((e) => sections.push(buildArenaLine(e)));
  }
  if (deck.length > 0) {
    if (sections.length > 0) sections.push('');
    sections.push('Deck');
    deck.forEach((e) => sections.push(buildArenaLine(e)));
  }
  if (sideboard.length > 0) {
    if (sections.length > 0) sections.push('');
    sections.push('Sideboard');
    sideboard.forEach((e) => sections.push(buildArenaLine(e)));
  }
  return sections.join('\n');
}

function buildPlainOutput(entries) {
  const commanders = entries.filter((e) => e.is_commander === true);
  const sideboard = entries.filter((e) => e.is_sideboard === true && e.is_commander !== true);
  const deck = entries.filter((e) => e.is_commander !== true && e.is_sideboard !== true);

  const sections = [];
  if (commanders.length > 0) {
    sections.push('[Commander]');
    commanders.forEach((e) => sections.push(buildPlainLine(e)));
  }
  if (deck.length > 0) {
    if (sections.length > 0) sections.push('');
    sections.push('[Maindeck]');
    deck.forEach((e) => sections.push(buildPlainLine(e)));
  }
  if (sideboard.length > 0) {
    if (sections.length > 0) sections.push('');
    sections.push('[Sideboard]');
    sideboard.forEach((e) => sections.push(buildPlainLine(e)));
  }
  return sections.join('\n');
}

// --- Component ---

function defaultFormat(collectionType) {
  return collectionType === 'DECK' ? FORMAT_ARENA : FORMAT_CSV_MOXFIELD;
}

const ExportModal = ({ open, onClose, collectionName, entries, collectionType }) => {
  const isDeck = collectionType === 'DECK';
  const [format, setFormat] = useState(defaultFormat(collectionType));
  const [copied, setCopied] = useState(false);

  const allEntries = entries || [];
  const arenaExcluded = allEntries.filter((e) => !e.games || !e.games.includes('arena'));
  const arenaIncluded = allEntries.filter((e) => e.games && e.games.includes('arena'));
  const allExcluded = format === FORMAT_ARENA && arenaIncluded.length === 0 && allEntries.length > 0;

  const isCopyFormat = format === FORMAT_ARENA || format === FORMAT_PLAIN;
  const showArenaWarning = format === FORMAT_ARENA && arenaExcluded.length > 0 && !allExcluded;

  const handleFormatChange = (value) => {
    setFormat(value);
    setCopied(false);
  };

  const handleAction = async () => {
    const safeName = (collectionName || 'collection').replace(/[^a-zA-Z0-9_-]/g, '_');

    if (format === FORMAT_ARENA) {
      await navigator.clipboard.writeText(buildArenaOutput(arenaIncluded));
      setCopied(true);
      setTimeout(() => setCopied(false), COPY_RESET_DELAY_MS);
    } else if (format === FORMAT_PLAIN) {
      await navigator.clipboard.writeText(buildPlainOutput(allEntries));
      setCopied(true);
      setTimeout(() => setCopied(false), COPY_RESET_DELAY_MS);
    } else {
      const csvFormat = format === FORMAT_CSV_MOXFIELD ? FORMAT_MOXFIELD : FORMAT_DECKBOX;
      const csvSuffix = format === FORMAT_CSV_MOXFIELD ? 'moxfield' : 'deckbox';
      downloadCSV(buildCSV(allEntries, csvFormat), `${safeName}_${csvSuffix}.csv`);
      onClose();
    }
  };

  const handleClose = () => {
    setFormat(defaultFormat(collectionType));
    setCopied(false);
    onClose();
  };

  const actionLabel = isCopyFormat
    ? (copied ? 'Copied!' : 'Copy to Clipboard')
    : 'Export CSV';

  const isDisabled = allEntries.length === 0 || allExcluded;

  return (
    <Modal open={open} onClose={handleClose} size="sm">
      <Modal.Header>
        <Modal.Title>Export</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p style={{ marginBottom: SPACING.marginBottom, color: COLORS.muted }}>
          Export <strong style={{ color: COLORS.white }}>{collectionName}</strong> ({allEntries.length} entries).
        </p>

        <div style={{ marginBottom: SPACING.marginBottom }}>
          <label style={{ fontSize: FONT.labelSize, color: COLORS.muted, marginBottom: SPACING.labelMarginBottom, display: 'block' }}>Format</label>
          <RadioGroup value={format} onChange={handleFormatChange}>
            {isDeck && <Radio value={FORMAT_ARENA}>MTG Arena (Copy to Clipboard)</Radio>}
            {isDeck && <Radio value={FORMAT_PLAIN}>Plain Text / MTGO (Copy to Clipboard)</Radio>}
            <Radio value={FORMAT_CSV_MOXFIELD}>Moxfield CSV (Download)</Radio>
            <Radio value={FORMAT_CSV_DECKBOX}>Deckbox CSV (Download)</Radio>
          </RadioGroup>
        </div>

        {showArenaWarning && (
          <Message type="warning" style={{ marginBottom: SPACING.marginBottom, fontSize: FONT.warningSize }}>
            <strong>{arenaExcluded.length} card(s) in this deck are not available on MTG Arena and will be skipped:</strong>
            <ul style={{ marginTop: 6, marginBottom: 0, paddingLeft: 20 }}>
              {arenaExcluded.map((e) => (
                <li key={e.name}>{e.name}</li>
              ))}
            </ul>
          </Message>
        )}

        {allExcluded && (
          <Message type="error" style={{ marginBottom: SPACING.marginBottom, fontSize: FONT.warningSize }}>
            No cards in this deck are available on MTG Arena.
          </Message>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={handleClose} appearance="subtle">
          {isCopyFormat ? 'Close' : 'Cancel'}
        </Button>
        <Button
          appearance="primary"
          onClick={handleAction}
          disabled={isDisabled}
        >
          {actionLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ExportModal;
